import React, { useState, useMemo, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useVersioningStore } from "@/stores/versioning/versioning-store";
import {
  DraftManagerProps,
  DraftFilter,
  DraftViewMode,
  DraftSortBy,
  DraftAriaLabels,
  CreateDraftData,
} from "./types";
import { DraftFilters } from "./DraftFilters";
import { DraftCard } from "./DraftCard";
import { DraftCreateDialog } from "./DraftCreateDialog";
import { DraftPreview } from "./DraftPreview";
import { DraftReviewDialog } from "./DraftReviewDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  FileText,
  Grid3X3,
  List,
  Table,
  Filter,
  Eye,
  EyeOff,
  RefreshCw,
  Clock,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const defaultAriaLabels: DraftAriaLabels = {
  manager: "Draft management interface",
  draft: (draft) =>
    `Draft ${draft.name} - ${draft.status} - created ${draft.createdAt.toLocaleDateString()}`,
  actions: {
    edit: "Edit draft",
    publish: "Publish draft",
    delete: "Delete draft",
    preview: "Preview draft",
    approve: "Approve draft",
    reject: "Reject draft",
  },
  filters: {
    status: "Filter by status",
    author: "Filter by author",
    search: "Search drafts",
    dateRange: "Filter by date range",
  },
  create: {
    button: "Create new draft",
    dialog: "Create draft dialog",
    name: "Draft name",
    description: "Draft description",
  },
};

export const DraftManager: React.FC<DraftManagerProps> = ({
  projectId,
  viewMode = "grid",
  filter: initialFilter,
  showFilters = true,
  showCreateButton = true,
  maxHeight = "600px",
  onDraftSelect,
  onDraftCreate,
  onDraftUpdate,
  onDraftPublish,
  onDraftDelete,
  onDraftReview,
  className,
}) => {
  // Zustand store hooks
  const {
    drafts,
    currentDraft,
    isLoading,
    error,
    metadata,
    createDraft,
    updateDraft,
    deleteDraft,
    publishDraft,
    submitDraftForReview,
    approveDraft,
    rejectDraft,
    initializeVersioning,
    refreshVersionData,
  } = useVersioningStore();

  // Local state
  const [localViewMode, setLocalViewMode] = useState<DraftViewMode>(viewMode);
  const [showFiltersPanel, setShowFiltersPanel] = useState(showFilters);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [previewDraftId, setPreviewDraftId] = useState<string | null>(null);
  const [reviewDraftId, setReviewDraftId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<DraftSortBy>("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Default filter
  const defaultFilter: DraftFilter = {
    status: [],
    authors: [],
    searchQuery: "",
    dateRange: { start: null, end: null },
  };

  const [filter, setFilter] = useState<DraftFilter>({
    ...defaultFilter,
    ...initialFilter,
  });

  // Initialize versioning when component mounts
  useEffect(() => {
    if (projectId) {
      initializeVersioning(projectId);
    }
  }, [projectId, initializeVersioning]);

  // Extract unique authors for filter options
  const availableAuthors = useMemo(() => {
    const authors = new Set<string>();
    drafts.forEach((draft) => authors.add(draft.createdBy));
    return Array.from(authors);
  }, [drafts]);

  // Filter and sort drafts
  const filteredAndSortedDrafts = useMemo(() => {
    let filtered = drafts.filter((draft) => {
      // Status filter
      if (filter.status.length > 0 && !filter.status.includes(draft.status)) {
        return false;
      }

      // Author filter
      if (
        filter.authors.length > 0 &&
        !filter.authors.includes(draft.createdBy)
      ) {
        return false;
      }

      // Date range filter
      if (filter.dateRange.start && draft.createdAt < filter.dateRange.start) {
        return false;
      }
      if (filter.dateRange.end && draft.createdAt > filter.dateRange.end) {
        return false;
      }

      // Search query filter
      if (filter.searchQuery) {
        const query = filter.searchQuery.toLowerCase();
        const searchableText = [draft.name, draft.description, draft.createdBy]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchableText.includes(query)) return false;
      }

      return true;
    });

    // Sort drafts
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "createdAt":
          comparison = (new Date(a.createdAt)).getTime() - (new Date(b.createdAt)).getTime();
          break;
        case "updatedAt":
          comparison = (new Date(a.updatedAt)).getTime() - (new Date(b.updatedAt)).getTime();
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = 0;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [drafts, filter, sortBy, sortOrder]);

  // Event handlers
  const handleDraftSelect = useCallback(
    (draft: (typeof drafts)[0]) => {
      setSelectedDraftId(draft.id);
      onDraftSelect?.(draft);
    },
    [onDraftSelect],
  );

  const handleCreateDraft = useCallback(
    async (data: CreateDraftData) => {
      try {
        const snapshot = await useVersioningStore
          .getState()
          .generateSnapshot(projectId);
        const newDraft = await createDraft({
          name: data.name,
          description: data.description,
          branchId: data.branchId,
          snapshot,
          changes: [], // Would be populated with actual changes
        });

        setShowCreateDialog(false);
        toast.success(`Draft "${data.name}" created successfully`);
        onDraftCreate?.();

        // Auto-select the new draft
        setSelectedDraftId(newDraft.id);
      } catch (error) {
        toast.error("Failed to create draft");
      }
    },
    [projectId, createDraft, onDraftCreate],
  );

  const handleDraftUpdate = useCallback(
    async (draftId: string, updates: Partial<(typeof drafts)[0]>) => {
      try {
        await updateDraft(draftId, updates);
        toast.success("Draft updated successfully");
        onDraftUpdate?.(draftId, updates);
      } catch (error) {
        toast.error("Failed to update draft");
      }
    },
    [updateDraft, onDraftUpdate],
  );

  const handleDraftPublish = useCallback(
    async (draftId: string) => {
      try {
        const publishedVersion = await publishDraft(draftId);
        toast.success(`Draft published as version ${publishedVersion.version}`);
        onDraftPublish?.(draftId);
      } catch (error) {
        toast.error("Failed to publish draft");
      }
    },
    [publishDraft, onDraftPublish],
  );

  const handleDraftDelete = useCallback(
    async (draftId: string) => {
      try {
        await deleteDraft(draftId);
        toast.success("Draft deleted successfully");

        if (selectedDraftId === draftId) {
          setSelectedDraftId(null);
        }

        onDraftDelete?.(draftId);
      } catch (error) {
        toast.error("Failed to delete draft");
      }
    },
    [deleteDraft, selectedDraftId, onDraftDelete],
  );

  const handleDraftReview = useCallback(
    async (
      draftId: string,
      action: "approve" | "reject",
      comments?: string,
    ) => {
      try {
        if (action === "approve") {
          await approveDraft(draftId);
          toast.success("Draft approved successfully");
        } else {
          await rejectDraft(draftId, comments);
          toast.success("Draft rejected");
        }

        setReviewDraftId(null);
        onDraftReview?.(draftId, action, comments);
      } catch (error) {
        toast.error(`Failed to ${action} draft`);
      }
    },
    [approveDraft, rejectDraft, onDraftReview],
  );

  const handleRefresh = useCallback(async () => {
    try {
      await refreshVersionData();
      toast.success("Draft data refreshed");
    } catch (error) {
      toast.error("Failed to refresh draft data");
    }
  }, [refreshVersionData]);

  // Get selected draft
  const selectedDraft = selectedDraftId
    ? drafts.find((d) => d.id === selectedDraftId)
    : null;
  const previewDraft = previewDraftId
    ? drafts.find((d) => d.id === previewDraftId)
    : null;
  const reviewDraft = reviewDraftId
    ? drafts.find((d) => d.id === reviewDraftId)
    : null;

  if (error) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center">
            <p className="text-destructive mb-2">
              Failed to load draft manager
            </p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn("w-full", className)}
      role="region"
      aria-label={defaultAriaLabels.manager}
    >
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 pb-4">
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg font-semibold">Draft Manager</CardTitle>
          {metadata.totalDrafts > 0 && (
            <Badge variant="secondary">{metadata.totalDrafts} drafts</Badge>
          )}
          {metadata.pendingDrafts > 0 && (
            <Badge
              variant="outline"
              className="border-orange-500 text-orange-600"
            >
              <Clock className="w-3 h-3 mr-1" />
              {metadata.pendingDrafts} pending review
            </Badge>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* View Mode Switcher */}
          <Tabs
            value={localViewMode}
            onValueChange={(value) => setLocalViewMode(value as DraftViewMode)}
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="grid" className="flex items-center space-x-1">
                <Grid3X3 className="w-4 h-4" />
                <span className="hidden sm:inline">Grid</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center space-x-1">
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
              <TabsTrigger
                value="table"
                className="flex items-center space-x-1"
              >
                <Table className="w-4 h-4" />
                <span className="hidden sm:inline">Table</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Sort Options */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortBy(field as DraftSortBy);
              setSortOrder(order as "asc" | "desc");
            }}
            className="px-3 py-1 border rounded-md text-sm bg-background"
            aria-label="Sort drafts"
          >
            <option value="updatedAt-desc">Latest Updated</option>
            <option value="updatedAt-asc">Oldest Updated</option>
            <option value="createdAt-desc">Latest Created</option>
            <option value="createdAt-asc">Oldest Created</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="status-asc">Status A-Z</option>
          </select>

          {/* Filters Toggle */}
          {showFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              aria-label={showFiltersPanel ? "Hide filters" : "Show filters"}
            >
              <Filter className="w-4 h-4" />
              {showFiltersPanel ? (
                <EyeOff className="w-4 h-4 ml-1" />
              ) : (
                <Eye className="w-4 h-4 ml-1" />
              )}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            aria-label="Refresh drafts"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>

          {/* Create Draft Button */}
          {showCreateButton && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              aria-label={defaultAriaLabels.create.button}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Draft
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Filters Panel */}
        {showFiltersPanel && showFilters && (
          <div className="border-b p-4 bg-muted/30">
            <DraftFilters
              filter={filter}
              onChange={setFilter}
              availableAuthors={availableAuthors}
            />
          </div>
        )}

        {/* Draft Content */}
        <div
          className="p-4"
          style={{ maxHeight, overflowY: "auto" }}
          role="main"
          aria-live="polite"
          aria-label="Draft list content"
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">
                Loading drafts...
              </span>
            </div>
          ) : filteredAndSortedDrafts.length === 0 ? (
            <div className="text-center py-8">
              {drafts.length === 0 ? (
                <div>
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">No drafts yet</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create your first draft to get started with version
                    management.
                  </p>
                  {showCreateButton && (
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Draft
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-muted-foreground mb-2">
                    No drafts match the current filters.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilter(defaultFilter)}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div
              className={cn(
                "space-y-4",
                localViewMode === "grid" &&
                  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 space-y-0",
                localViewMode === "table" && "space-y-0",
              )}
            >
              {filteredAndSortedDrafts.map((draft) => (
                <DraftCard
                  key={draft.id}
                  draft={draft}
                  viewMode={localViewMode}
                  isSelected={selectedDraftId === draft.id}
                  showActions={true}
                  onSelect={() => handleDraftSelect(draft)}
                  onEdit={() => handleDraftUpdate(draft.id, {})}
                  onPublish={() => handleDraftPublish(draft.id)}
                  onDelete={() => handleDraftDelete(draft.id)}
                  onReview={(action, comments) =>
                    handleDraftReview(draft.id, action, comments)
                  }
                  onPreview={() => setPreviewDraftId(draft.id)}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Dialogs */}
      <DraftCreateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreateDraft={handleCreateDraft}
      />

      {previewDraft && (
        <DraftPreview
          draft={previewDraft}
          onClose={() => setPreviewDraftId(null)}
          onEdit={() => handleDraftUpdate(previewDraft.id, {})}
          onPublish={() => handleDraftPublish(previewDraft.id)}
          onDelete={() => handleDraftDelete(previewDraft.id)}
        />
      )}

      {reviewDraft && (
        <DraftReviewDialog
          draft={reviewDraft}
          open={true}
          onOpenChange={(open) => !open && setReviewDraftId(null)}
          onSubmitReview={(action, comments) => handleDraftReview(reviewDraft.id, action, comments)}
        />
      )}
    </Card>
  );
};

export default DraftManager;
